// gloss
const MIME_TYPE_EXTENSIONS: Record<string, string> = {
  'video/webm': 'webm',
  'video/mp4': 'mp4',
  'video/quicktime': 'mov',
  'video/x-matroska': 'mkv',
  'video/ogg': 'ogv',
  'audio/webm': 'webm',
  'audio/mp4': 'm4a',
  'audio/mpeg': 'mp3',
  'audio/ogg': 'ogg',
  'audio/wav': 'wav',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/svg+xml': 'svg',
  'application/pdf': 'pdf',
};

// gloss
const KNOWN_EXTENSIONS = new Set([
  ...Object.values(MIME_TYPE_EXTENSIONS),
  'jpeg',
  'm4v',
  'avi',
  'wmv',
  'flv',
  '3gp',
  'ogv',
  'oga',
  'aac',
  'flac',
  'bmp',
  'tiff',
  'ico',
  'csv',
  'txt',
  'json',
  'zip',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
]);

// gloss
export const baseMimeType = (mimeType: string | null | undefined): string =>
  (mimeType ?? '').split(';')[0]?.trim().toLowerCase() ?? '';

// gloss
export const mediaExtensionFromMimeType = (mimeType: string | null | undefined): string | null => {
  const baseType = baseMimeType(mimeType);
  if (!baseType) return null;

  const mapped = MIME_TYPE_EXTENSIONS[baseType];
  if (mapped) return mapped;

  const subtype = baseType.split('/')[1]?.replace(/^x-/, '') ?? '';
  return KNOWN_EXTENSIONS.has(subtype) ? subtype : null;
};

const splitFileName = (fileName: string): { base: string; extension: string } => {
  const dotIndex = fileName.lastIndexOf('.');
  if (dotIndex <= 0) return { base: fileName, extension: '' };
  return { base: fileName.slice(0, dotIndex), extension: fileName.slice(dotIndex + 1) };
};

// gloss
const repairExtension = (extension: string): string | null => {
  const candidate = extension.toLowerCase().split(/[_;,=\s]/)[0] ?? '';
  return KNOWN_EXTENSIONS.has(candidate) ? candidate : null;
};

// gloss
const looksLikeMangledExtension = (extension: string): boolean => /[_;,=]/.test(extension);

// gloss
export const normalizeMediaFileName = (fileName: string, mimeType?: string | null): string => {
  const { base, extension } = splitFileName(fileName);
  if (!base) return fileName;

  const repaired = extension ? repairExtension(extension) : null;
  if (repaired) return `${base}.${repaired}`;

  const fromMimeType = mediaExtensionFromMimeType(mimeType);
  if (!fromMimeType) return fileName;
  if (extension && !looksLikeMangledExtension(extension)) return `${fileName}.${fromMimeType}`;
  return `${base}.${fromMimeType}`;
};

// gloss
const decodeFileNameSegment = (segment: string): string => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};

// gloss
const fileNameFromUrl = (url: string): string => {
  try {
    return decodeFileNameSegment(new URL(url).pathname.split('/').pop() ?? '');
  } catch {
    return decodeFileNameSegment(url.split('/').pop()?.split(/[?#]/)[0] ?? '');
  }
};

// gloss
export const uploadedFileName = (url: URL): string =>
  decodeFileNameSegment(url.pathname.split('/').pop() ?? '').replace(/^\d+-/, '') || 'File';

// gloss
export type MediaDownloadFileNameOptions = {
  mimeType?: string | null;
  baseName?: string | null;
  fallbackBaseName?: string;
};

// gloss
export const mediaDownloadFileName = (
  url: string,
  { mimeType, baseName, fallbackBaseName = 'download' }: MediaDownloadFileNameOptions = {},
): string => {
  const urlFileName = fileNameFromUrl(url);
  const { base, extension } = splitFileName(urlFileName);
  const resolvedBase = baseName?.trim() || base.trim() || fallbackBaseName;
  const withExtension = extension ? `${resolvedBase}.${extension}` : resolvedBase;

  return normalizeMediaFileName(withExtension, mimeType);
};

const VIDEO_EXTENSIONS = new Set(['mp4', 'webm', 'ogg', 'ogv', 'mov', 'avi', 'wmv', 'flv', 'mkv', '3gp', 'm4v']);

// gloss
export const isVideoMediaURL = (url: string | null | undefined): boolean => {
  if (!url) return false;

  const extension = mediaDownloadFileName(url).split('.').pop()?.toLowerCase();
  return extension !== undefined && VIDEO_EXTENSIONS.has(extension);
};
